import { Image } from "@themes/pollinator/ui/Image";
import { Type } from "@themes/pollinator/ui/Type";
import { marketingType } from "../../marketing.type";

export type QuoteVariant = "band" | "centered" | "statement";

export type Props = {
  /**
   * The words as Outlast punctuates them, quotation marks and all. A node
   * rather than a string, because the design colours phrases inside one.
   */
  quote: React.ReactNode;
  /** Who said it; the one statement quote carries no attribution at all */
  attribution?: string;
  /** Their portrait, where one has been cleared for use */
  portraitUrl?: string;
  /**
   * How the design sets it: a band is a portrait beside left-set lines, the
   * pair centred on the page as a block; centered and statement are centred
   * display-size text on their own.
   */
  variant?: QuoteVariant;
  /**
   * The width of the words in a band. The design sizes each band's column to
   * its own quote, which is what decides where the lines break.
   */
  copyWidthClassName?: string;
  className?: string;
};

/**
 * One participant, in their own words: bold italics, as the design sets every
 * quote on the page, with the attribution upright beneath.
 *
 * The portrait is optional because consent for it is collected separately from
 * the quote, and a quote we are allowed to print is worth printing whether or
 * not a face came with it.
 */
export function MarketingQuote({
  quote,
  attribution,
  portraitUrl,
  variant = "band",
  copyWidthClassName = "lg:max-w-[880px]",
  className,
}: Props) {
  const isBand = variant === "band";

  return (
    <figure
      className={`flex mx-auto ${
        isBand
          ? "flex-col gap-6 w-full md:gap-4 lg:gap-6 lg:w-fit lg:flex-row lg:items-start"
          : `flex-col gap-6 items-center text-center ${
              variant === "statement" ? "max-w-[918px]" : "max-w-[880px]"
            }`
      } ${className ?? ""}`}
      data-testid="marketing-quote"
    >
      {portraitUrl && (
        <Image
          src={portraitUrl}
          alt={attribution ?? "Pollinator participant"}
          // Small and centred above the words on a phone or tablet, beside them
          // on a desktop
          className="shrink-0 rounded-full overflow-hidden w-16 h-16 self-center lg:w-24 lg:h-24 lg:self-start"
          imageClassName="object-cover w-full h-full"
        />
      )}
      <div
        className={`flex flex-col ${
          isBand ? `gap-4 w-full lg:w-auto ${copyWidthClassName}` : "gap-6 items-center"
        }`}
      >
        <Type
          variant="body1"
          as="blockquote"
          className={`max-w-none ${
            isBand
              ? marketingType.quote
              : `${marketingType.display} font-bold italic`
          }`}
        >
          {quote}
        </Type>
        {attribution && (
          <Type
            variant="body2"
            as="figcaption"
            className={`max-w-none ${
              isBand ? marketingType.caption : marketingType.body
            }`}
          >
            {`– ${attribution}`}
          </Type>
        )}
      </div>
    </figure>
  );
}

export default MarketingQuote;
